import React from 'react';
import { Loader2 } from 'lucide-react';
import { HEADS, TEMPERATURE_COPY, type HeadId, type StageId } from '../data/content';
import { formatPct, valueColor, type WordInfo } from '../lib/model';
import { cx } from '../lib/hooks';
import type { SceneToken } from '../scene/TokenObject';
import { TokenText } from './TokenText';
export type StageControlsProps = {
  stage: StageId;
  tokens: SceneToken[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  neighbors: WordInfo[];
  clusterLabel: string;
  head: HeadId;
  onHead: (head: HeadId) => void;
  attention: number[];
  candidates: {
    text: string;
    prob: number;
  }[];
  temperature: number;
  onTemperature: (t: number) => void;
  onSample: () => void;
  onStartOver: () => void;
  atCap: boolean;
  picking: number | null;
  hasGenerated: boolean;
};
export function StageControls(props: StageControlsProps) {
  switch (props.stage) {
    case 'tokens':
      return <TokenList {...props} data-id="element-226" data-mp="23zfu8zu7yafe" />;
    case 'embeddings':
      return <VectorReadout {...props} data-id="element-227" data-mp="2rsfwi5mxn22t" />;
    case 'meaning':
      return <Neighbors {...props} data-id="element-228" data-mp="2rsy2htzecunx" />;
    case 'attention':
      return <AttentionPanel {...props} data-id="element-229" data-mp="2ws29qf2rgibm" />;
    case 'layers':
      return <LayerLegend data-id="element-230" data-mp="2wtlog18rqlba" />;
    case 'prediction':
      return <PredictionPanel {...props} data-id="element-231" data-mp="2i3egifge0kou" />;
    default:
      return null;
  }
}
function TokenList({
  tokens,
  selectedIndex,
  onSelect
}: StageControlsProps) {
  const chars = tokens.reduce((n, t) => n + t.text.length, 0);
  return <div className="mt-3" data-id="element-232" data-mp="290dbki39czmr">
      <div className="flex flex-wrap gap-1.5" data-id="element-233" data-mp="294axtcl4u7av">
        {tokens.map((t, i) => <button key={t.key} type="button" onClick={() => onSelect(i)} className={cx('flex items-center gap-2 rounded-lg border px-2 py-1 text-[12px] transition-colors', i === selectedIndex ? 'border-white/35 bg-white/[0.1]' : 'border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06]')} data-id="element-234" data-mp="2e2cfpu6to0kr" data-mpi={i}>
            <span className="h-2 w-2 rounded-full" style={{
          backgroundColor: t.color
        }} data-id="element-235" data-mp="ubh9p7yl2ld" />
            <TokenText text={t.text} className="text-[#EDEEF2]" data-id="element-236" data-mp="rypylgb9fvn" />
            <span className="font-mono text-[#5D6272]" data-id="element-237" data-mp="3rzuoaez0sdoj">{t.id}</span>
          </button>)}
      </div>
      <p className="mt-2 text-[12px] text-[#5D6272]" data-id="element-238" data-mp="3n21kfsl6vvrf">
        {tokens.length} tokens from {chars} characters
      </p>
    </div>;
}
function VectorReadout({
  tokens,
  selectedIndex
}: StageControlsProps) {
  const token = tokens[selectedIndex];
  return <div className="mt-3" data-id="element-239" data-mp="el7wmecvjnah">
      <SelectedLabel token={token} suffix="looks up these 8 numbers" data-id="element-240" data-mp="en14ty34yfkl" />
      <div className="mt-2 grid grid-cols-4 gap-1.5" data-id="element-241" data-mp="9uhpavkw1ad5">
        {token.vector.map((v, k) => <div key={k} className={cx('rounded-md py-1.5 text-center font-mono text-[12px]', v > 0.45 ? 'text-[#0B0C12]' : 'text-white/90')} style={{
        backgroundColor: valueColor(v)
      }} data-id="element-242" data-mp="5txuhynseiu6" data-mpi={k}>
            {v >= 0 ? '+' : '\u2212'}
            {Math.abs(v).toFixed(2)}
          </div>)}
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-[linear-gradient(90deg,#3B82F6,#1C1F2B,#F59E0B)]" data-id="element-243" data-mp="2l6f58kztckze" />
      <div className="mt-1 flex justify-between font-mono text-[11px] text-[#5D6272]" data-id="element-244" data-mp="2p80ezxh1wj5d">
        <span data-id="element-245" data-mp="2pad02ji3v9mn">{'\u2212'}1</span>
        <span data-id="element-246" data-mp="2u7da15i2swj1">0</span>
        <span data-id="element-247" data-mp="2u7wepkiptof4">+1</span>
      </div>
    </div>;
}
function Neighbors({
  tokens,
  selectedIndex,
  neighbors,
  clusterLabel
}: StageControlsProps) {
  const token = tokens[selectedIndex];
  return <div className="mt-3" data-id="element-248" data-mp="3449acnxtmcci">
      <SelectedLabel token={token} suffix={`sits with ${clusterLabel}`} data-id="element-249" data-mp="343gckcguax6i" />
      <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.08em] text-[#5D6272]" data-id="element-250" data-mp="38xaqez9rkhxo">
        Nearest words
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-251" data-mp="3czp8vi9itfof">
        {neighbors.map(n => <span key={n.norm} className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-2 py-1 font-mono text-[12px] text-[#EDEEF2]" data-id="element-252" data-mp="3hyiphjk3tmst">
            {n.display}
          </span>)}
      </div>
    </div>;
}
function AttentionPanel({
  tokens,
  selectedIndex,
  onSelect,
  head,
  onHead,
  attention
}: StageControlsProps) {
  const token = tokens[selectedIndex];
  const hiddenAfter = tokens.length - 1 - selectedIndex;
  const headDef = HEADS.find(h => h.id === head) ?? HEADS[0];
  return <div className="mt-3" data-id="element-253" data-mp="2q9xut6lf0lka">
      <div className="grid grid-cols-3 gap-1 rounded-full border border-white/[0.08] bg-white/[0.03] p-1" data-id="element-254" data-mp="2qb8twt3x1552">
        {HEADS.map(h => <button key={h.id} type="button" onClick={() => onHead(h.id)} className={cx('h-7 rounded-full text-[12px] font-medium transition-colors', h.id === head ? 'bg-white/[0.14] text-[#EDEEF2]' : 'text-[#9A9FB0] hover:text-[#EDEEF2]')} data-id="element-255" data-mp="2ld7f4kdivrze">
            {h.label}
          </button>)}
      </div>
      <p className="mt-2 text-[12px] leading-[18px] text-[#9A9FB0]" data-id="element-256" data-mp="ale7wp6jsj02">{headDef.description}</p>
      <div className="mt-3" data-id="element-257" data-mp="i4nk0q6fuvst">
        <SelectedLabel token={token} prefix="What" suffix="looks at" data-id="element-258" data-mp="n1dzbbaf5nd6" />
      </div>
      <div className="mt-2 space-y-1.5" data-id="element-259" data-mp="mzb0c1ragjwb">
        {attention.map((w, j) => <button key={tokens[j].key} type="button" onClick={() => onSelect(j)} className="grid w-full grid-cols-[92px_1fr_36px] items-center gap-2 text-left" data-id="element-260" data-mp="93peg3gcqg84" data-mpi={j}>
            <TokenText text={tokens[j].text} className="truncate text-[12px] text-[#C9CCD8]" data-id="element-261" data-mp="15s5eith387zh" />
            <span className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]" data-id="element-262" data-mp="15tznraizybnq">
              <span className="block h-full rounded-full transition-[width] duration-500" style={{
            width: `${w * 100}%`,
            backgroundColor: tokens[j].color
          }} data-id="element-263" data-mp="rynuz8oeiv9g" />
            </span>
            <span className="text-right font-mono text-[11px] text-[#9A9FB0]" data-id="element-264" data-mp="1oimtlv8zz52d">{formatPct(w)}</span>
          </button>)}
      </div>
      {hiddenAfter > 0 && <p className="mt-2 text-[12px] text-[#5D6272]" data-id="element-265" data-mp="1ydu0bwbdqnyv">
          It can't see the {hiddenAfter === 1 ? 'token' : `${hiddenAfter} tokens`} after it.
        </p>}
    </div>;
}
function LayerLegend() {
  return <div className="mt-3 space-y-2" data-id="element-266" data-mp="1pfru92csh54q">
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3" data-id="element-267" data-mp="1kfnan3gl3uaa">
        <div className="text-[12px] font-medium text-[#EDEEF2]" data-id="element-268" data-mp="1ffiujejl91kl">Attention</div>
        <div className="text-[12px] leading-[18px] text-[#9A9FB0]" data-id="element-269" data-mp="1fhluoreggb31">Tokens share information.</div>
      </div>
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3" data-id="element-270" data-mp="16ngw1lt2xvcy">
        <div className="text-[12px] font-medium text-[#EDEEF2]" data-id="element-271" data-mp="16l31de2bal5x">Feed-forward</div>
        <div className="text-[12px] leading-[18px] text-[#9A9FB0]" data-id="element-272" data-mp="11q77079lrlpn">
          Each vector is transformed on its own.
        </div>
      </div>
      <p className="font-mono text-[12px] text-[#5D6272]" data-id="element-273" data-mp="rqgeuc8ilisq">6 layers shown · GPT-3 has 96</p>
    </div>;
}
function PredictionPanel({
  candidates,
  temperature,
  onTemperature,
  onSample,
  onStartOver,
  atCap,
  picking,
  hasGenerated
}: StageControlsProps) {
  return <div className="mt-3" data-id="element-274" data-mp="mn5tnm899z13">
      <div className="flex items-baseline justify-between" data-id="element-275" data-mp="rjw9q9k28umi">
        <span className="text-[12px] font-medium text-[#EDEEF2]" data-id="element-276" data-mp="rictgiyemjkq">Temperature</span>
        <span className="font-mono text-[12px] text-[#EDEEF2]" data-id="element-277" data-mp="wiqzjvgg3jg4">{temperature.toFixed(2)}</span>
      </div>
      <input type="range" min={0.1} max={2} step={0.05} value={temperature} onChange={e => onTemperature(Number(e.target.value))} aria-label="Temperature" className="temp-slider mt-1.5 w-full" data-id="element-278" data-mp="3v2z69pzguqcv" />
      <div className="flex justify-between text-[11px] text-[#5D6272]" data-id="element-279" data-mp="dlpr20non17f">
        <span data-id="element-280" data-mp="3cb6g03bs7j8u">Sharper</span>
        <span data-id="element-281" data-mp="3c9lic4u67k6r">Flatter</span>
      </div>
      <p className="mt-2 text-[12px] leading-[18px] text-[#9A9FB0]" data-id="element-282" data-mp="3c8k6hy5bytnz">{TEMPERATURE_COPY}</p>

      <div className="mt-3 space-y-1" data-id="element-283" data-mp="3h7n2yu78vrkc">
        {candidates.map((c, k) => <div key={c.text} className={cx('grid grid-cols-[92px_1fr_36px] items-center gap-2 rounded-md px-1 py-0.5 transition-colors', picking === k && 'bg-white/[0.1]')} data-id="element-284" data-mp="zbc09bxw52v1" data-mpi={k}>
            <TokenText text={c.text} className="truncate text-[12px] text-[#C9CCD8]" data-id="element-285" data-mp="14a6h48cashci" />
            <span className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]" data-id="element-286" data-mp="1i9oa6zwmmgro">
              <span className={cx('block h-full rounded-full transition-[width] duration-300', k === 0 ? 'bg-[#EDEEF2]' : 'bg-[#5A6386]')} style={{
            width: `${c.prob * 100}%`
          }} data-id="element-287" data-mp="1i7l8him42z74" />
            </span>
            <span className="text-right font-mono text-[11px] text-[#9A9FB0]" data-id="element-288" data-mp="1n2hdrkgczj26">{formatPct(c.prob)}</span>
          </div>)}
      </div>
      <p className="mt-2 text-[11px] leading-4 text-[#5D6272]" data-id="element-289" data-mp="1wz3xu70v2635">
        Real models score every token in the vocabulary. We show the top 6.
      </p>

      {atCap ? <button type="button" onClick={onStartOver} className="mt-3 h-9 w-full rounded-full bg-white text-[13px] font-semibold text-[#07080C] transition-opacity hover:opacity-90" data-id="element-290" data-mp="2gijdsxatxgvh">
          Start over
        </button> : <button type="button" onClick={onSample} disabled={picking !== null} className="mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-full bg-white text-[13px] font-semibold text-[#07080C] transition-opacity hover:opacity-90 disabled:opacity-80" data-id="element-291" data-mp="2bjpx6w08x9r3">
          {picking !== null && <Loader2 className="h-4 w-4 animate-spin" data-id="element-292" data-mp="1sut24px0yd7t" />}
          Sample next token
        </button>}
      {hasGenerated && !atCap && <button type="button" onClick={onStartOver} className="mt-2 h-8 w-full rounded-full text-[12px] font-medium text-[#9A9FB0] transition-colors hover:bg-white/[0.06] hover:text-[#EDEEF2]" data-id="element-293" data-mp="1dyk57v0lwbyk">
          Start over
        </button>}
    </div>;
}
function SelectedLabel({
  token,
  prefix,
  suffix
}: {
  token: SceneToken;
  prefix?: string;
  suffix: string;
}) {
  return <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-[#9A9FB0]" data-id="element-294" data-mp="1li9s9zndazss">
      {prefix && <span data-id="element-295" data-mp="1qg1sdgy61s82">{prefix}</span>}
      <span className="rounded-md border border-white/10 bg-white/[0.06] px-1.5 py-0.5" data-id="element-296" data-mp="1qhcw5grm2zz6">
        <TokenText text={token.text} className="text-[12px]" data-id="element-297" data-mp="2e7787w43wkid" />
      </span>
      <span data-id="element-298" data-mp="2j3nzgz6lrezo">{suffix}</span>
    </div>;
}